<template>
  <PaginaAdministrar
    titulo="Cadastro de Moedas"
    subtitulo="Registo de moedas para operações de câmbio e cotações"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="true"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Nome ou Código ISO</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Fonte de Cotação</label>
          <select class="form-select" v-model="filtros.lstFonteCotacao" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option v-for="f in listas.fontesCotacao" :key="f.idListaSistemaItem" :value="f.idListaSistemaItem">{{ f.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3">{{ erro }}</div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="moedas"
      :loading="loading"
      empty-message="Nenhuma moeda encontrada."
      row-key="idMoeda"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarMoeda($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idMoeda }}</span>
      </template>
      <template #iso="{ row }">
        <span class="badge badge-ghost-primary">{{ row.codigoISO }}</span>
      </template>

      <template #tipo="{ row }">
        <span class="badge" :class="row.tipoMoeda === 'A' ? 'badge-ghost-info' : 'badge-ghost-warning'">
          {{ row.tipoMoeda === 'A' ? 'Indireta' : 'Direta' }}
        </span>
      </template>

      <template #status="{ row }">
        <span :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper">
          <button class="btn-icon text-primary" @click="editarMoeda(row)" title="Editar"><i class="bi bi-pencil-fill"></i></button>
          <button class="btn-icon text-danger" @click="confirmarExclusao(row)" title="Excluir"><i class="bi bi-trash-fill"></i></button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const moedas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  status: [],
  fontesCotacao: []
})

const filtros = reactive({
  busca: '',
  lstFonteCotacao: '',
  lstStatus: ''
})

const colunas = [
  { field: 'idMoeda', label: 'ID', width: 60, sortable: true, slotName: 'id' },
  { field: 'codigoISO', label: 'ISO', width: 80, sortable: true, slotName: 'iso' },
  { field: 'nome', label: 'Nome', sortable: true, class: 'fw-medium' },
  { field: 'tipoMoeda', label: 'Tipo', align: 'center', sortable: true, slotName: 'tipo' },
  { field: 'lstFonteCotacao', label: 'Fonte Cotação', sortable: true },
  { field: 'lstStatus', label: 'Status', width: 100, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstFonteCotacao: (row) => getFonteTexto(row.lstFonteCotacao),
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const carregarListas = async () => {
  try {
    const [resStatus, resFontes] = await Promise.all([
      api.get('/ListasSistema/lookup/ST'),
      api.get('/ListasSistema/lookup/MOE_FONTE_COTACAO')
    ])
    listas.status = resStatus.data
    listas.fontesCotacao = resFontes.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const statusObj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return statusObj ? statusObj.textoLista : idStatus
}

const getFonteTexto = (idFonte) => {
  if (!idFonte) return '-'
  const fonteObj = listas.fontesCotacao.find(f => f.idListaSistemaItem === idFonte || f.idListaClienteItem === idFonte)
  return fonteObj ? fonteObj.textoLista : idFonte
}

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (filtros.busca) params.append('termoBusca', filtros.busca)
    if (filtros.lstFonteCotacao) params.append('lstFonteCotacao', filtros.lstFonteCotacao)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Moedas?${params.toString()}`)
    moedas.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as moedas.'
    moedas.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.lstFonteCotacao = ''; filtros.lstStatus = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarMoeda = async (moeda) => {
  try {
    loading.value = true
    const response = await api.get(`/Moedas/${moeda.idMoeda}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da moeda.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const confirmarExclusao = (moeda) => {
  emit('excluir-item', moeda)
}

onMounted(async () => {
  await carregarListas()
  buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<style scoped>
</style>
